import { Link } from "react-router-dom";
import type { ReceiptSource, ReceiptStatus, Trip } from "@/api/types";
import { IconButton } from "./IconButton";
import { ReceiptStatusBadge } from "./ReceiptStatusBadge";
import { RelativeTime } from "./RelativeTime";
import "./in-trip-header.css";

export interface InTripHeaderProps {
  trip: Trip;
  /**
   * Back-into-trip route. Default `/t/<trip_id>/receipts`. Rendered as a
   * real <Link> — never `navigate(-1)` — so the destination is
   * deterministic across every entry path (came-from-upload, deep-link,
   * from-list). See components.md §6.6.
   */
  backTo?: string;
  /** Default «До чеків» — names the destination, reassuring the trip is intact. */
  backLabel?: string;
  /** Drives the meta-line <ReceiptStatusBadge>. Omit to hide the meta line. */
  status?: ReceiptStatus;
  /**
   * ADR-0031: when `manual`, the meta badge shows «Вручну» instead of the
   * status label. A manual receipt is born confirmed so the meta line always
   * renders (never a parsing/parse_failed hide).
   */
  source?: ReceiptSource;
  /**
   * Feeds <RelativeTime> on the meta line (AC-19.2). Hidden when null —
   * which is the case for parsing / parse_failed where there is no
   * meaningful "updated" moment to show.
   */
  updatedAt?: string | null;
  /** Opens the share affordance (US-04). Always present. */
  onShare: () => void;
}

/**
 * Slim sticky in-trip header for deep receipt-flow screens that are not
 * tab roots (receipt-detail and its sub-states). Replaces the ad-hoc
 * DetailHeader and the brand-only BrandHeader, both of which could lead
 * the user out of the trip (to `/`) — losing the slug = losing the trip
 * under the no-accounts model. See components.md §6.6.
 *
 * Guarantees the navigation invariant: the only exit is a deterministic
 * back-into-trip <Link>, plus share. No tab row (that lives on the
 * receipts list, one tap away).
 */
export function InTripHeader({
  trip,
  backTo,
  backLabel = "До чеків",
  status,
  source,
  updatedAt,
  onShare,
}: InTripHeaderProps) {
  const target = backTo ?? `/t/${trip.trip_id}/receipts`;
  const isManual = source === "manual";
  // The status meta line is hidden entirely for parsing / parse_failed:
  // there is no settled "updated" moment to surface, and the wireframe
  // collapses the meta line in those states. A manual receipt (born confirmed)
  // always shows the «Вручну» meta line.
  const showMeta =
    isManual ||
    (status !== undefined && status !== "parsing" && status !== "parse_failed");

  return (
    <header className="wp-ith">
      <div className="wp-ith__row">
        <Link
          to={target}
          className="wp-ith__back"
          aria-label={backLabel}
          data-testid="in-trip-back"
        >
          <span aria-hidden="true" className="wp-ith__chevron">
            ‹
          </span>
          <span className="wp-ith__title" title={trip.name}>
            {trip.name}
          </span>
          {showMeta ? (
            <span className="wp-ith__meta wp-ith__meta--inline">
              <span aria-hidden="true" className="wp-ith__dot">
                ·
              </span>
              <ReceiptStatusBadge status={status ?? "confirmed"} source={source} />
            </span>
          ) : null}
        </Link>
        <IconButton aria-label="Поділитися посиланням на поїздку" onClick={onShare}>
          ⤴
        </IconButton>
      </div>
      {showMeta ? (
        <div className="wp-ith__meta wp-ith__meta--stacked">
          <ReceiptStatusBadge status={status ?? "confirmed"} source={source} />
          {updatedAt ? (
            <>
              <span aria-hidden="true" className="wp-ith__dot">
                ·
              </span>
              <RelativeTime value={updatedAt} prefix="оновлено" />
            </>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
